import { itemsApi } from "#/api/itemsApi";
import { itemTemplateApi } from "#/api/itemTemplateApi";
import type { RfidTag } from "#/types/rfidTag";
import { getRandomColor } from "#/utils/ColorUtils";
import { manifestApi } from "#/api/manifestApi";
import HawbSelect from "#/components/manifests/HawbSelect";
import { useShipmentManifestsEnabled } from "#/hooks/useShipmentManifestsEnabled";
import type { HawbOption, LinkTagResult } from "#/types/manifest";
import { formatHawbLabel } from "#/utils/manifestUtils";
import { logger } from "#/utils/logger";
import { Badge, Button, Modal, SegmentedControl, Select, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

interface BulkCreateItemsModalProps {
  opened: boolean;
  onClose: () => void;
  selectedTags: RfidTag[];
  onSuccess: () => void;
}

const BulkCreateItemsModal = ({
  opened,
  onClose,
  selectedTags,
  onSuccess,
}: BulkCreateItemsModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const manifestsEnabled = useShipmentManifestsEnabled();
  /** "template" creates new items; "manifest" links tags to a consignment's existing untagged items. */
  const [mode, setMode] = useState<"template" | "manifest">("template");
  const [selectedHawb, setSelectedHawb] = useState<HawbOption | null>(null);
  const [failedLinks, setFailedLinks] = useState<LinkTagResult[]>([]);
  const byManifest = manifestsEnabled && mode === "manifest";
  const tooManyTags = selectedHawb !== null && selectedTags.length > selectedHawb.untagged_count;

  const { data: { templates = [] } = {}, isLoading: templatesLoading } = useQuery({
    queryKey: ["templates-for-bulk-create"],
    queryFn: async () => {
      const response = await itemTemplateApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch templates");
      }
      return {
        templates: response.data.data,
      };
    },
    enabled: opened,
    staleTime: 1000 * 60,
  });

  const bulkCreateMutation = useMutation({
    mutationFn: async ({ templateId, rfidIds }: { templateId: number; rfidIds: number[] }) => {
      const response = await itemsApi.bulkCreateFromTemplate(templateId, rfidIds);
      if (!response.data) {
        throw new Error("Failed to create items");
      }
      return response.data;
    },
    onSuccess: (items) => {
      notifications.show({
        title: t("common.success"),
        message: t("rfidTags.bulkCreate.success", { count: items.length }),
        color: "green",
      });
      handleClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t("rfidTags.bulkCreate.error");
      notifications.show({
        title: t("common.error"),
        message: errorMessage,
        color: "red",
      });
    },
  });

  const linkTagsMutation = useMutation({
    mutationFn: async ({ hawbId, rfidIds }: { hawbId: number; rfidIds: number[] }) => {
      const response = await manifestApi.linkTags(hawbId, rfidIds);
      return response.data.results ?? [];
    },
    onSuccess: (results) => {
      const linked = results.filter((result) => result.ok).length;
      const failed = results.filter((result) => !result.ok);
      if (linked > 0) {
        notifications.show({
          title: t("common.success"),
          message: t("rfidTags.bulkCreate.linkSuccess", {
            count: linked,
            hawb: selectedHawb ? formatHawbLabel(selectedHawb) : "",
          }),
          color: "green",
        });
        onSuccess();
      }
      if (failed.length > 0) {
        // Keep the modal open so the user sees which tags were not linked.
        setFailedLinks(failed);
        return;
      }
      handleClose();
    },
    onError: (error: unknown) => {
      // The API client already shows the error toast (e.g. not enough untagged items).
      logger.error("Failed to link tags to consignment:", error);
    },
  });

  const handleClose = () => {
    setSelectedTemplateId(null);
    setSelectedHawb(null);
    setFailedLinks([]);
    setMode("template");
    onClose();
  };

  const handleLink = () => {
    if (!selectedHawb || tooManyTags) return;
    setFailedLinks([]);
    linkTagsMutation.mutate({
      hawbId: selectedHawb.id,
      rfidIds: selectedTags.map((tag) => tag.id),
    });
  };

  const handleCreate = () => {
    if (selectedTemplateId) {
      bulkCreateMutation.mutate({
        templateId: parseInt(selectedTemplateId),
        rfidIds: selectedTags.map((tag) => tag.id),
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("rfidTags.bulkCreate.title")}
      closeOnClickOutside={false}
      size="xl"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="flex w-full flex-col gap-4">
        {manifestsEnabled && (
          <SegmentedControl
            value={mode}
            onChange={(value) => {
              setMode(value === "manifest" ? "manifest" : "template");
              setFailedLinks([]);
            }}
            disabled={bulkCreateMutation.isPending || linkTagsMutation.isPending}
            data={[
              { label: t("rfidTags.bulkCreate.byTemplate"), value: "template" },
              { label: t("rfidTags.bulkCreate.byManifest"), value: "manifest" },
            ]}
          />
        )}

        <Text size="sm" c="dimmed">
          {byManifest
            ? t("rfidTags.bulkCreate.manifestDescription", { count: selectedTags.length })
            : t("rfidTags.bulkCreate.description", { count: selectedTags.length })}
        </Text>

        {byManifest ? (
          <div className="flex flex-col gap-2">
            <HawbSelect
              label={t("rfidTags.bulkCreate.hawbLabel")}
              required
              hasUntagged
              value={selectedHawb?.id ?? null}
              onChange={(_, option) => {
                setSelectedHawb(option);
                setFailedLinks([]);
              }}
              disabled={linkTagsMutation.isPending}
            />
            {tooManyTags && selectedHawb && (
              <Text size="sm" c="red">
                {t("rfidTags.bulkCreate.tooManyTags", {
                  hawb: formatHawbLabel(selectedHawb),
                  untagged: selectedHawb.untagged_count,
                  selected: selectedTags.length,
                })}
              </Text>
            )}
            {failedLinks.length > 0 && (
              <div className="flex flex-col gap-1 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <span className="font-semibold">
                  {t("rfidTags.bulkCreate.linkFailedTitle", { count: failedLinks.length })}
                </span>
                {failedLinks.map((result) => (
                  <span key={result.rfid} className="font-mono">
                    {result.rfid}:{" "}
                    {result.error === "already_linked"
                      ? t("rfidTags.bulkCreate.alreadyLinked")
                      : (result.error ?? t("rfidTags.bulkCreate.error"))}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : templatesLoading ? (
          <Text size="sm" c="dimmed">
            {t("rfidTags.bulkCreate.loadingTemplates")}
          </Text>
        ) : templates.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("rfidTags.bulkCreate.noTemplatesFound")}
          </Text>
        ) : (
          <Select
            label={t("rfidTags.bulkCreate.templateLabel")}
            placeholder={t("rfidTags.bulkCreate.templatePlaceholder")}
            searchable
            withAsterisk
            data={templates.map((template) => ({
              label: template.name,
              value: template.id.toString(),
            }))}
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            description={
              selectedTemplateId
                ? templates.find((t) => t.id.toString() === selectedTemplateId)?.description ||
                  t("rfidTags.bulkCreate.noDescription")
                : t("rfidTags.bulkCreate.chooseTemplate")
            }
          />
        )}

        <div className="flex w-full flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <span className="text-xs font-bold text-slate-600">
            {t("rfidTags.bulkCreate.selectedTags", { count: selectedTags.length })}
          </span>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <Badge
                key={tag.id}
                color={getRandomColor(tag.rfid)}
                variant="dot"
                size="md"
                className="font-mono"
              >
                {tag.rfid}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={handleClose}
            disabled={bulkCreateMutation.isPending || linkTagsMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          {byManifest ? (
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="black"
                disabled={
                  !selectedHawb ||
                  tooManyTags ||
                  selectedTags.length === 0 ||
                  linkTagsMutation.isPending ||
                  !canWrite()
                }
                onClick={handleLink}
                loading={linkTagsMutation.isPending}
              >
                {t("rfidTags.bulkCreate.linkButton", { count: selectedTags.length })}
              </Button>
            </PermissionTooltip>
          ) : (
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="black"
                disabled={
                  !selectedTemplateId ||
                  templates.length === 0 ||
                  bulkCreateMutation.isPending ||
                  !canWrite()
                }
                onClick={handleCreate}
                loading={bulkCreateMutation.isPending}
              >
                {t("rfidTags.bulkCreate.createButton", { count: selectedTags.length })}
              </Button>
            </PermissionTooltip>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BulkCreateItemsModal;
